// Main App component for the React SPA

import React from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

// Pages
import LoginPage from '../pages/auth/LoginPage'
import DashboardPage from '../pages/dashboard/DashboardPage'
import PitchesPage from '../pages/dashboard/PitchesPage'
import ContentPage from '../pages/dashboard/ContentPage'
import LibraryPage from '../pages/dashboard/LibraryPage'
import CalendarPage from '../pages/dashboard/CalendarPage'
import AnalyticsPage from '../pages/dashboard/AnalyticsPage'
import SettingsPage from '../pages/dashboard/SettingsPage'

// Components
import AuthGuard from './AuthGuard'
import DashboardLayout from './layout/DashboardLayout'
import ThemeProvider from '../contexts/ThemeProvider'
import { Toaster } from './ui/Toaster'

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount: number, error: any) => {
        // Don't retry on 401/403 errors
        if (error?.statusCode === 401 || error?.statusCode === 403) {
          return false
        }
        return failureCount < 3
      },
      staleTime: 60 * 1000, // 1 minute
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
})

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <Router>
          <div className="min-h-screen bg-background text-foreground">
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              
              {/* Protected routes */}
              <Route path="/dashboard" element={
                <AuthGuard>
                  <DashboardLayout>
                    <DashboardPage />
                  </DashboardLayout>
                </AuthGuard>
              } />
              
              <Route path="/pitches" element={
                <AuthGuard>
                  <DashboardLayout>
                    <PitchesPage />
                  </DashboardLayout>
                </AuthGuard>
              } />
              
              <Route path="/content" element={
                <AuthGuard>
                  <DashboardLayout>
                    <ContentPage />
                  </DashboardLayout>
                </AuthGuard>
              } />
              
              <Route path="/library" element={
                <AuthGuard>
                  <DashboardLayout>
                    <LibraryPage />
                  </DashboardLayout>
                </AuthGuard>
              } />
              
              <Route path="/calendar" element={
                <AuthGuard>
                  <DashboardLayout>
                    <CalendarPage />
                  </DashboardLayout>
                </AuthGuard>
              } />
              
              <Route path="/analytics" element={
                <AuthGuard>
                  <DashboardLayout>
                    <AnalyticsPage />
                  </DashboardLayout>
                </AuthGuard>
              } />
              
              <Route path="/settings" element={
                <AuthGuard>
                  <DashboardLayout>
                    <SettingsPage />
                  </DashboardLayout>
                </AuthGuard>
              } />
              
              {/* Default redirect */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              
              {/* 404 fallback */}
              <Route path="*" element={
                <div className="min-h-screen flex items-center justify-center">
                  <div className="text-center">
                    <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
                      404
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400 mb-8">
                      Page not found
                    </p>
                    <a 
                      href="/dashboard" 
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                    >
                      Go to Dashboard
                    </a>
                  </div>
                </div>
              } />
            </Routes>
            
            {/* Global toast notifications */}
            <Toaster />
          </div>
        </Router>
      </ThemeProvider>
      
      {/* React Query DevTools (only in development) */}
      {process.env.NODE_ENV === 'development' && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
    </QueryClientProvider>
  )
}

// Error Boundary Component
export class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('App Error Boundary:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="max-w-md w-full space-y-8">
            <div className="text-center">
              <h2 className="mt-6 text-3xl font-extrabold text-gray-900 dark:text-white">
                Something went wrong
              </h2>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                We encountered an unexpected error. Please refresh the page or try again later.
              </p>
              <div className="mt-6">
                <button
                  onClick={() => window.location.reload()}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  Refresh Page
                </button>
              </div>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}